import React from 'react';
import { useHistory } from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import Snackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';

import DateFnsUtils from '@date-io/date-fns';
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker,
} from '@material-ui/pickers';

import MaterialTable from 'material-table'

import CeremonyBuilderDialog from './CeremonyBuilderDialog';
import ConfirmDialog from './ConfirmDialog';
import CoupleBalanceModal from './CoupleBalanceModal';


const tableRef = React.createRef();

const dateKeys = {
    start: 'enduringpromises.com.couples.table.dates.start',
    end: 'enduringpromises.com.couples.table.dates.end'
};
const sessionKeys = {
    search: 'enduringpromises.com.couples.table.search'
};

const useStyles = makeStyles(theme => ({
    formField: {
      marginLeft: theme.spacing(2),
    },
    formButton: {
        marginLeft: theme.spacing(1),
    },
    mutedLink: {
        textDecoration: 'none',
        color: theme.palette.grey[500],
    },
    link: {
        color: theme.palette.primary.light,
        cursor: 'pointer',
    }
}));

const customSorter = (a, b) => true;

const MaterialTableTable = (props) => {
    const {
        dateStart,
        dateEnd
    } = props;

    const [builderDialogOpen, setBuilderDialogOpen] = React.useState(false);
    const handleBuilderDialogClose = () => {
        setBuilderDialogOpen(false);
    }

    const [currentCouple, setCurrentCouple] = React.useState(null);

    const displayEditCeremony = rowData => event => {
        setCurrentCouple(rowData);
        setBuilderDialogOpen(true);
    }

    const downloadCeremony = rowData => event => {
        window.open(`/api/couples/${rowData.id}/download-ceremony`, "_blank");
    }

    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const sendApprovalEmail = id => event => {
        event.preventDefault();
        const data = {
            _token: window._token
        }
        new Promise((resolve, reject) => {
            fetch(`/api/approvals/${id}/send-email`, {
                method: 'POST',
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setSnackbarMessage('Successfully sent email!');
                    setOpenSnackbar(true);
                    if (tableRef.current) {
                        tableRef.current.onQueryChange();
                    }
                })
                .catch(reason => {
                    alert('An error prevented emailing the couple. Please refresh the page and try again.');
                })
                .finally(() => {
                    resolve();
                });
        });
    }

    const classes = useStyles();

    const [openDeleteDialog, setDeleteDialogOpen] = React.useState(false);
    const handleDeleteClose = action => () => {
        if (action == 'agree') {
            deleteCouple(currentCouple.id).then(() => {
                setSnackbarMessage('Successfully deleted couple');
                setOpenSnackbar(true);
                tableRef.current.onQueryChange();
            });
        }
        setDeleteDialogOpen(false);
    };

    const handleDelete = rowData => {
        setCurrentCouple(rowData);
        setDeleteDialogOpen(true);
    }

    const deleteCouple = id => {
        const data = {
            _token: window._token
        }
        return new Promise((resolve, reject) => {
            fetch(`/api/couples/${id}`, {
                method: 'DELETE',
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setDeleteDialogOpen(false);
                    resolve();
                })
                .catch(reason => {
                    setDeleteDialogOpen(false);
                    alert('An error prevented deleting the couple. Please refresh the page and try again.');
                    reject();
                })
        });
    }

    const [openCoupleBalanceModal, setOpenCoupleBalanceModal] = React.useState(false);

    const handleBalanceClick = rowData => () => {
        setCurrentCouple(rowData);
        setOpenCoupleBalanceModal(true);
    }

    const formatNumber = number => {
        return (number !== '' && number !== undefined) ? parseFloat(number).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        }) : '';
    }

    const [state, setState] = React.useState({
        columns: [
            { title: 'Bride', field: 'bride', filtering: false, sorting: false },
            { title: 'Groom', field: 'groom', filtering: false, sorting: false },
            { title: 'Date of Inquiry', field: 'inquiry_date', type: 'date', filtering: false, customSort: customSorter },
            { title: 'Officiant', field: 'officiant_name', filtering: false, sorting: false },
            { 
                title: 'Ceremony',
                filtering: false,
                sorting: false,
                cellStyle: {
                    minWidth: 130
                },
                render: rowData => (
                    <Box>
                        <Tooltip title="Edit Ceremony">
                            <IconButton size='small' color="inherit" onClick={displayEditCeremony(rowData)}>
                                <Icon fontSize='small'>visibility</Icon>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Ceremony">
                            <IconButton size='small' color="inherit" onClick={downloadCeremony(rowData)}>
                                <Icon fontSize='small'>cloud_download</Icon>
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            },
            { title: 'Wedding Date', field: 'wedding_date_formatted', type: 'date', filtering: false, defaultSort: 'asc', customSort: customSorter },
            {
                title: 'Balance',
                field: 'balance',
                type: 'currency',
                filtering: false,
                sorting: false,
                render: rowData => (
                    <span className={classes.link} onClick={handleBalanceClick(rowData)}>
                        {formatNumber(rowData.balance)}
                    </span>
                )
            },
            {
                title: 'Approve in Person',
                filtering: false,
                sorting: false,
                render: rowData => {
                    const signUrl = `/approval/form?couple_hash=${rowData.hash}`;
                    const hasNotAgreed = !rowData.couple_agree_date || rowData.couple_agree_date === '0000-00-00 00:00:00';
                    return (
                        <React.Fragment>
                            {hasNotAgreed && <a href={signUrl} target="_blank">Sign</a>}
                            {!hasNotAgreed && <span>Complete</span>}
                        </React.Fragment>
                    );
                },
            },
            {
                title: 'Approve by Email',
                filtering: false,
                sorting: false,
                render: rowData => {
                    const signUrl = `/approval/form?couple_hash=${rowData.hash}`;
                    const hasNotAgreed = !rowData.couple_agree_date || rowData.couple_agree_date === '0000-00-00 00:00:00';
                    const hasNotBeenSentEmail = !rowData.couple_sign_send || rowData.couple_sign_send === '0000-00-00 00:00:00';
                    return (
                        <React.Fragment>
                            {hasNotAgreed && hasNotBeenSentEmail && <a href="#" onClick={sendApprovalEmail(rowData.id)}>Send</a>}
                            {hasNotAgreed && !hasNotBeenSentEmail && <span><a className={classes.mutedLink} href="#" onClick={sendApprovalEmail(rowData.id)}>Already Sent</a></span>}
                        </React.Fragment>
                    );
                },
            },
            {
                title: 'Status',
                field: 'couple_type',
                filtering: true,
                sorting: false,
                lookup: {
                    'Lead': 'Lead',
                    'Follow-Up': 'Follow-Up',
                    'Pending': 'Pending',
                    'Booked': 'Booked',
                    'Complete': 'Complete',
                    'Cancelled': 'Cancelled'
                }
            },
        ],
    });

    const history = useHistory();

    function handleClick(event, rowData) {
        const newUrl = `/admin/couples/${rowData.id}`;
        if (event.metaKey) {
            window.open(newUrl);
            return;
        }
        history.push(newUrl);
    }

    const pageSizeOptions = [30, 50, 100, 250];
    const [resultTotal, setResultTotal] = React.useState(0);
    const filteredPageSizeOptions = total => total > 0 ? pageSizeOptions : [0];

    const [isActiveFilter, setIsActiveFilter] = React.useState(true);
    const handleIsActiveChange = event => {
        setIsActiveFilter(event.target.checked);
        if (tableRef.current) {
            const query = tableRef.current.state.query;
            query.page = 0;
            query.is_active = isActiveFilter;
            tableRef.current.onQueryChange();
        }
    }

    const fetchData = query =>
        new Promise((resolve, reject) => {
            let url = '/api/couples?';

            /* set the search to local storage */
            localStorage.setItem(sessionKeys.search, query.search);

            /* TOOD: find better way to do this */
            query.weddingStart = query.weddingStart ? query.weddingStart : dateStart;
            query.weddingEnd = query.weddingEnd ? query.weddingEnd : dateEnd;
            query.is_active = isActiveFilter;
            query.orderBy = query.orderBy ? query.orderBy : { field: 'wedding_date' };

            /* TODO: use orderBy and filters */
            url += 'per_page=' + query.pageSize;
            url += '&page=' + (query.page + 1);
            url += '&search=' + query.search;
            url += '&orderBy=' + (query.orderBy ? query.orderBy.field : '');
            url += '&orderDirection=' + (query.orderDirection ? query.orderDirection : 'asc');
            url += '&weddingStart=' + dateFormatter(query.weddingStart);
            url += '&weddingEnd=' + dateFormatter(query.weddingEnd);
            url += '&is_active=' + query.is_active;
            if (query.filters.length) {
                url += '&' + query.filters.map(x => `${x.column.field}=`+encodeURI(x.value)).join('&')
            }
            fetch(url)
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setResultTotal(result.total);
                    result.data.map(user => {
                        user.bride = `${user.bride_first_name} ${user.bride_last_name}`;
                        user.groom = `${user.groom_first_name} ${user.groom_last_name}`;
                        user.officiant_name = user.officiant ? `${user.officiant.first_name} ${user.officiant.last_name}` : null;
                    });
                    resolve({
                        data: result.data,
                        page: result.current_page - 1,
                        totalCount: result.total,
                    })
                })
            }
        )

    const isAdmin = window.isAdmin;
    const deletableCoupleTypes = ['Lead', 'Follow-Up', 'Pending'];

    const defaultSearchText = localStorage.getItem(sessionKeys.search) || '';

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box component="span" style={{marginLeft: -10}}>
                    <Switch id="is-active-filter" checked={isActiveFilter} onChange={handleIsActiveChange} title="Is Active" color="primary" />
                </Box>
               <Typography component="label" className="MuiFormLabel-root MuiInputLabel-shrink">
                   {isActiveFilter ? 'Active couples' : 'Inactive couples'}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <MaterialTable
                    title="Couples List"
                    columns={state.columns}
                    data={fetchData}
                    actions={[
                        {
                            icon: 'edit',
                            tooltip: 'Edit Couple',
                            onClick: handleClick
                        },
                        isAdmin ? rowData => ({
                            icon: 'delete',
                            tooltip: deletableCoupleTypes.includes(rowData.couple_type) ? 'Delete Couple' : '',
                            disabled: !deletableCoupleTypes.includes(rowData.couple_type),
                            onClick: (event, rowData) => {
                                handleDelete(rowData);
                            }
                        }) : null,
                        {
                            icon: 'refresh',
                            tooltip: 'Refresh Data',
                            isFreeAction: true,
                            onClick: () => tableRef.current && tableRef.current.onQueryChange(),
                        }
                    ].filter(x => x !== null)}
                    components={{
                        Container: Box,
                    }}
                    options={{
                        showTitle: false,
                        pageSize: 30,
                        pageSizeOptions: filteredPageSizeOptions(resultTotal),
                        exportButton: true,
                        loadingType: 'linear',
                        emptyRowsWhenPaging: false,
                        filtering: true,
                        sorting: true,
                        debounceInterval: 600,
                        padding: 'dense',
                        searchText: defaultSearchText,
                    }}
                    tableRef={tableRef}
                />
                <ConfirmDialog
                    title="Confirm delete"
                    text="Are you sure you wish to delete this couple?"
                    agreeText="Delete"
                    agreeColor="secondary"
                    declineText="Cancel"
                    handleClose={handleDeleteClose}
                    open={openDeleteDialog}
                />
                {openCoupleBalanceModal && <CoupleBalanceModal
                    open={openCoupleBalanceModal}
                    setOpen={setOpenCoupleBalanceModal}
                    couple={currentCouple}
                />}
                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    open={openSnackbar}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    message={snackbarMessage}
                    action={
                        <React.Fragment>
                            <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </React.Fragment>
                    }
                />
                <CeremonyBuilderDialog open={builderDialogOpen} handleClose={handleBuilderDialogClose} scroll='paper' couple={currentCouple} />
            </Grid>
        </Grid>
    );
};

const dateFormatter = date => {
    return [
        date.getFullYear(), date.getMonth()+1, date.getDate()
    ].join('-');
}

const WeddingDatePicker = (props) => {
    const {
        dateStart,
        dateEnd
    } = props;

    const [selectedStartDate, setselectedStartDate] = React.useState(dateStart);
    const [selectedEndDate, setSelectedEndDate] = React.useState(dateEnd);
    const classes = useStyles();

    const handleDateChange = date => {
        setselectedStartDate(date);
    };

    const handleEndDateChange = date => {
        setSelectedEndDate(date);
    };

    const handleDateSet = () => {
        localStorage.setItem(dateKeys.start, selectedStartDate);
        localStorage.setItem(dateKeys.end, selectedEndDate);
        if (tableRef.current) {
            const query = tableRef.current.state.query;
            query.weddingStart = selectedStartDate;
            query.weddingEnd = selectedEndDate;
            query.page = 0;
            tableRef.current.onQueryChange();
        }
    };

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid
                container
                direction="row"
                justify="flex-start"
                alignItems="baseline"
            >
                <KeyboardDatePicker
                    margin="normal"
                    id="date-picker-dialog"
                    label="Wedding date filter"
                    helperText="Start Date"
                    format="MM/dd/yyyy"
                    value={selectedStartDate}
                    onChange={handleDateChange}
                    KeyboardButtonProps={{
                        'aria-label': 'change date',
                    }}
                />
                <KeyboardDatePicker
                    margin="normal"
                    id="date-end-picker-dialog"
                    format="MM/dd/yyyy"
                    helperText="End Date"
                    value={selectedEndDate}
                    onChange={handleEndDateChange}
                    KeyboardButtonProps={{
                        'aria-label': 'change date',
                    }}
                    className={classes.formField}
                />
                <Button className={classes.formButton} onClick={handleDateSet}>Set</Button>
            </Grid>
        </MuiPickersUtilsProvider>
    );
};

const CouplesList = () => {
    const defaultDateStart = localStorage.getItem(dateKeys.start) ? new Date(localStorage.getItem(dateKeys.start)) : new Date();
    const defaultDateEnd = localStorage.getItem(dateKeys.end) ? new Date(localStorage.getItem(dateKeys.end)) : new Date(new Date().setMonth(new Date().getMonth() + 12));
    localStorage.setItem(dateKeys.start, defaultDateStart);
    localStorage.setItem(dateKeys.end, defaultDateEnd);

    const [dateStart, setDateStart] = React.useState(defaultDateStart);
    const [dateEnd, setDateEnd] = React.useState(defaultDateEnd);
    return (
        <div style={{ maxWidth: '100%' }}>
            <Typography variant="h6">Couples List</Typography>
            <WeddingDatePicker dateStart={dateStart} dateEnd={dateEnd} />
            <MaterialTableTable dateStart={dateStart} dateEnd={dateEnd} />
        </div>
    );
}

export default CouplesList;
